import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FileText, Search, Calendar, Download } from 'lucide-react';
import { vendorApi } from '../../services/api';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import StatusBadge from '../../components/StatusBadge';
import PdfButton from '../../components/PdfButton';
import { formatCurrency, formatDate, formatPeriod, isBlankDate } from '../../utils/format';
import { exportCsv } from '../../utils/exportCsv';

const typeKey = (invoice: any) => (invoice.documentType === 'Credit Memo' ? 'creditMemo' : 'invoice');
const detailPath = (invoice: any) => `/invoices/${typeKey(invoice)}/${encodeURIComponent(invoice.no)}`;

/** Paid / open / overdue, when BC reports it (older Settled.Portal versions don't) */
export function paymentStatus(invoice: { closed?: boolean; paidDate?: string; dueDate?: string }) {
  if (invoice.closed === undefined) return null;
  if (invoice.closed) {
    return { tone: 'green' as const, label: isBlankDate(invoice.paidDate) ? 'Reglerad' : `Betald ${formatDate(invoice.paidDate)}` };
  }
  const overdue = !isBlankDate(invoice.dueDate) && new Date(invoice.dueDate as string) < new Date(new Date().toDateString());
  return overdue
    ? { tone: 'brass' as const, label: `Förföll ${formatDate(invoice.dueDate)}` }
    : { tone: 'turquoise' as const, label: isBlankDate(invoice.dueDate) ? 'Obetald' : `Förfaller ${formatDate(invoice.dueDate)}` };
}

export default function Invoices() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState('');

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => (await vendorApi.me()).data,
  });

  const { data: invoicesData, isLoading, isError } = useQuery({
    queryKey: ['vendor', 'invoices'],
    queryFn: async () => (await vendorApi.getInvoices()).data,
  });

  useEffect(() => {
    if (error) navigate('/login');
  }, [error, navigate]);

  const invoices: any[] = invoicesData?.invoices || [];

  const filteredInvoices = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return invoices.filter((invoice) => {
      const matchesSearch = !term || [invoice.no, invoice.vendorInvoiceNo].some((v) => String(v || '').toLowerCase().includes(term));
      const matchesMonth = !monthFilter || invoice.postingDate?.startsWith(monthFilter);
      return matchesSearch && matchesMonth;
    });
  }, [invoices, searchTerm, monthFilter]);

  const months = useMemo(
    () => Array.from(new Set(invoices.map((i) => i.postingDate?.substring(0, 7)).filter(Boolean))).sort().reverse() as string[],
    [invoices]
  );

  const exportRows = () =>
    exportCsv(`avrakningar-${new Date().toISOString().slice(0, 10)}`, [
      { header: 'Typ', value: (i: any) => (i.documentType === 'Credit Memo' ? 'Kreditnota' : 'Faktura') },
      { header: 'Nummer', value: (i: any) => i.no },
      { header: 'Er referens', value: (i: any) => i.vendorInvoiceNo },
      { header: 'Period från', value: (i: any) => i.settlementPeriodStart },
      { header: 'Period till', value: (i: any) => i.settlementPeriodEnd },
      { header: 'Bokföringsdatum', value: (i: any) => i.postingDate },
      { header: 'Förfallodatum', value: (i: any) => i.dueDate },
      { header: 'Belopp exkl. moms', value: (i: any) => i.amount || 0 },
      { header: 'Belopp inkl. moms', value: (i: any) => i.amountIncludingVAT || 0 },
      { header: 'Betald', value: (i: any) => (i.closed === undefined ? '' : i.closed ? 'Ja' : 'Nej') },
      { header: 'Betaldatum', value: (i: any) => i.paidDate },
    ], filteredInvoices);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar logoUrl={vendorData?.tenant?.logoUrl} companyName={vendorData?.tenant?.companyName} />

      <div className="flex flex-1 flex-col">
        <TopBar userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username} />

        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Avräkningar</h1>
              <p className="mt-1 text-gray-600">Dina bokförda avräkningsfakturor och kreditnotor – öppna en för specifikation och PDF</p>
            </div>
            <button
              type="button"
              onClick={exportRows}
              disabled={filteredInvoices.length === 0}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink ring-1 ring-inset ring-gray-200 transition-all duration-300 ease-fluid hover:ring-blue-300 active:scale-[0.98] disabled:opacity-50"
            >
              <Download className="h-4 w-4" aria-hidden />
              Exportera till Excel
            </button>
          </div>

          <div className="card mb-6 p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden />
                <input
                  type="search"
                  aria-label="Sök"
                  placeholder="Sök på nummer eller er referens…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-full border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden />
                <select
                  aria-label="Månad"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="w-full rounded-full border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-primary"
                >
                  <option value="">Alla månader</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {new Date(m + '-01').toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-600">
              Visar {filteredInvoices.length} av {invoices.length} dokument
            </p>
          </div>

          <div className="card">
            {isLoading ? (
              <div className="p-12 text-center text-gray-500">Laddar avräkningar…</div>
            ) : isError ? (
              <p role="alert" className="p-6 text-sm text-red-800">Avräkningarna kunde inte hämtas. Försök igen om en stund.</p>
            ) : filteredInvoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Typ', 'Nummer', 'Period', 'Bokförd', 'Status'].map((h) => (
                        <th key={h} scope="col" className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          {h}
                        </th>
                      ))}
                      <th scope="col" className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Exkl. moms
                      </th>
                      <th scope="col" className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Inkl. moms
                      </th>
                      <th scope="col" className="px-5 py-3">
                        <span className="sr-only">PDF</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredInvoices.map((invoice) => {
                      const credit = invoice.documentType === 'Credit Memo';
                      const status = paymentStatus(invoice);
                      return (
                        <tr
                          key={`${typeKey(invoice)}-${invoice.no}`}
                          onClick={() => navigate(detailPath(invoice))}
                          className="cursor-pointer transition-colors hover:bg-blue-50/60"
                        >
                          <td className="whitespace-nowrap px-5 py-3.5">
                            <StatusBadge tone={credit ? 'brass' : 'gray'}>{credit ? 'Kreditnota' : 'Faktura'}</StatusBadge>
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5">
                            <Link
                              to={detailPath(invoice)}
                              onClick={(e) => e.stopPropagation()}
                              className="font-mono text-sm font-medium text-ink hover:text-blue-700"
                            >
                              {invoice.no}
                            </Link>
                            {invoice.vendorInvoiceNo && <span className="block text-xs text-gray-500">{invoice.vendorInvoiceNo}</span>}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-700">
                            {formatPeriod(invoice.settlementPeriodStart, invoice.settlementPeriodEnd)}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-700">{formatDate(invoice.postingDate)}</td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-sm">
                            {status ? <StatusBadge tone={status.tone}>{status.label}</StatusBadge> : <span className="text-gray-500">Förfaller {formatDate(invoice.dueDate)}</span>}
                          </td>
                          <td className={`whitespace-nowrap px-5 py-3.5 text-right text-sm tabular-nums ${credit ? 'text-red-700' : 'text-gray-700'}`}>
                            {formatCurrency(invoice.amount)}
                          </td>
                          <td className={`whitespace-nowrap px-5 py-3.5 text-right text-sm font-medium tabular-nums ${credit ? 'text-red-700' : 'text-gray-900'}`}>
                            {formatCurrency(invoice.amountIncludingVAT)}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-right">
                            <span className="inline-flex gap-2">
                              <PdfButton documentType={typeKey(invoice)} documentNo={invoice.no} />
                              <PdfButton documentType={typeKey(invoice)} documentNo={invoice.no} format="excel" />
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <FileText className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden />
                <h2 className="mb-2 text-base font-semibold text-ink">Inga avräkningar hittades</h2>
                <p className="text-gray-600">
                  {searchTerm || monthFilter ? 'Prova att ändra sökning eller filter' : 'Det finns inga bokförda avräkningar ännu'}
                </p>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
